// /BlogHome.jsx
import React, { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  Avatar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArticleIcon from "@mui/icons-material/Article";
import EmailIcon from "@mui/icons-material/Email";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CategoryIcon from "@mui/icons-material/Category";
import TagIcon from "@mui/icons-material/Tag";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSEO, useJsonLD } from "../SEO";
import { CATEGORIES, TAGS, POSTS } from "./BlogData";

// Theme tokens
const ACCENT = "#F0B90B";
const PAGE_BG = "#0B0E11";
const SURFACE = "rgba(38,50,56,0.85)";
const PANEL_BG = "#263238";
const BORDER = "rgba(255,255,255,0.08)";
const TEXT = "#EAEFF2";
const MUTED = "#B0BEC5";

function SectionTitle({ icon, title }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      {icon}
      <Typography
        variant="overline"
        sx={{ color: ACCENT, letterSpacing: 1, fontWeight: 800 }}
      >
        {title}
      </Typography>
    </Stack>
  );
}

function CategoryPills({ current, onChange }) {
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap">
      <Chip
        label="All"
        onClick={() => onChange("all")}
        sx={{
          bgcolor: current === "all" ? "rgba(240,185,11,0.16)" : "transparent",
          border: `1px solid ${BORDER}`,
          color: TEXT,
        }}
      />
      {CATEGORIES.map((c) => (
        <Chip
          key={c.key}
          label={c.label}
          onClick={() => onChange(c.key)}
          sx={{
            bgcolor:
              current === c.key ? "rgba(240,185,11,0.16)" : "transparent",
            border: `1px solid ${BORDER}`,
            color: TEXT,
          }}
        />
      ))}
    </Stack>
  );
}

function TagCloud({ selectedTag, onSelect }) {
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap">
      {TAGS.map((t) => (
        <Chip
          key={t}
          icon={<TagIcon sx={{ color: ACCENT }} />}
          label={t}
          onClick={() => onSelect(t === selectedTag ? "" : t)}
          sx={{
            bgcolor:
              t === selectedTag ? "rgba(240,185,11,0.16)" : "transparent",
            border: `1px solid ${BORDER}`,
            color: TEXT,
          }}
        />
      ))}
    </Stack>
  );
}

function FeaturedGrid({ posts, onRead }) {
  return (
    <Grid container spacing={2}>
      {posts.map((p) => (
        <Grid item xs={12} md={4} key={p.id}>
          <Card
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              bgcolor: PANEL_BG,
              border: `1px solid ${BORDER}`,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <CardMedia
              component="img"
              src={p.hero || "https://picsum.photos/1200/630?blur=2"}
              alt={p.title}
              sx={{ height: 180, objectFit: "cover" }}
              loading="lazy"
            />
            <CardContent sx={{ flexGrow: 1 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <CategoryIcon sx={{ color: ACCENT, fontSize: 18 }} />
                <Typography variant="caption" sx={{ color: MUTED }}>
                  {CATEGORIES.find((c) => c.key === p.category)?.label ||
                    "General"}
                </Typography>
                <AccessTimeIcon sx={{ color: ACCENT, fontSize: 16, ml: 1 }} />
                <Typography variant="caption" sx={{ color: MUTED }}>
                  {p.readMins} min read
                </Typography>
              </Stack>
              <Typography
                variant="h6"
                sx={{ color: TEXT, fontWeight: 800, mb: 1 }}
              >
                {p.title}
              </Typography>
              <Typography variant="body2" sx={{ color: MUTED }}>
                {p.excerpt}
              </Typography>
            </CardContent>
            <Box sx={{ p: 2, pt: 0 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                {p.tags.slice(0, 3).map((t) => (
                  <Chip
                    key={t}
                    label={t}
                    size="small"
                    sx={{ color: TEXT, border: `1px solid ${BORDER}` }}
                  />
                ))}
              </Stack>
              <Button
                fullWidth
                variant="contained"
                onClick={() => onRead(p)}
                sx={{
                  bgcolor: ACCENT,
                  color: "#0B0E11",
                  fontWeight: 800,
                  ":hover": { bgcolor: "#ffd43b", color: "#0B0E11" },
                }}
              >
                Read Article
              </Button>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

function PostRow({ post, onRead }) {
  return (
    <Card
      sx={{
        display: "flex",
        gap: 2,
        bgcolor: PANEL_BG,
        border: `1px solid ${BORDER}`,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box sx={{ width: { xs: 120, md: 220 }, flexShrink: 0 }}>
        <CardMedia
          component="img"
          src={post.hero || "https://picsum.photos/1000/600?blur=2"}
          alt={post.title}
          sx={{ height: "100%", objectFit: "cover" }}
          loading="lazy"
        />
      </Box>
      <CardContent sx={{ flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <ArticleIcon sx={{ color: ACCENT, fontSize: 18 }} />
          <Typography variant="caption" sx={{ color: MUTED }}>
            {CATEGORIES.find((c) => c.key === post.category)?.label ||
              "General"}
          </Typography>
          <AccessTimeIcon sx={{ color: ACCENT, fontSize: 16, ml: 1 }} />
          <Typography variant="caption" sx={{ color: MUTED }}>
            {post.readMins} min read •{" "}
            {new Date(post.date).toLocaleDateString()}
          </Typography>
        </Stack>
        <Typography
          variant="h6"
          sx={{ color: TEXT, fontWeight: 800, mb: 0.75 }}
        >
          {post.title}
        </Typography>
        <Typography variant="body2" sx={{ color: MUTED, mb: 1.25 }}>
          {post.excerpt}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
          {post.tags.map((t) => (
            <Chip
              key={t}
              label={t}
              size="small"
              sx={{ color: TEXT, border: `1px solid ${BORDER}` }}
            />
          ))}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Avatar
            sx={{ width: 24, height: 24, bgcolor: "rgba(240,185,11,0.2)" }}
          >
            {post.author.name.charAt(0)}
          </Avatar>
          <Typography variant="caption" sx={{ color: MUTED }}>
            {post.author.name} • {post.author.role}
          </Typography>
        </Stack>
        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onRead(post)}
            sx={{
              borderColor: ACCENT,
              color: ACCENT,
              ":hover": { borderColor: "#ffd43b", color: "#ffd43b" },
            }}
          >
            Read more
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

function NewsletterCard() {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: `1px solid ${BORDER}`,
        bgcolor: SURFACE,
        backdropFilter: "blur(6px)",
      }}
    >
      <SectionTitle
        icon={<EmailIcon sx={{ color: ACCENT }} />}
        title="NEWSLETTER"
      />
      <Typography variant="body2" sx={{ color: MUTED, mb: 1.5 }}>
        Weekly briefings on product updates, market structure, and security best
        practices — crafted for traders and builders.
      </Typography>
      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          placeholder="Your email address"
          size="small"
          sx={{
            "& .MuiInputBase-root": { bgcolor: PANEL_BG, color: TEXT },
            "& fieldset": { borderColor: BORDER },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  sx={{ color: ACCENT }}
                  aria-label="subscribe"
                >
                  <EmailIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      <Typography
        variant="caption"
        sx={{ color: MUTED, mt: 1, display: "block" }}
      >
        No spam. Unsubscribe anytime.
      </Typography>
    </Paper>
  );
}

function TrendingList({ posts }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: `1px solid ${BORDER}`,
        bgcolor: SURFACE,
        backdropFilter: "blur(6px)",
      }}
    >
      <SectionTitle
        icon={<TrendingUpIcon sx={{ color: ACCENT }} />}
        title="TRENDING"
      />
      <List dense disablePadding>
        {posts.slice(0, 6).map((p) => (
          <ListItem key={p.id} sx={{ px: 0, py: 1 }}>
            <ListItemText
              primary={
                <Typography
                  variant="body2"
                  sx={{ color: TEXT, fontWeight: 700, lineHeight: 1.35 }}
                >
                  {p.title}
                </Typography>
              }
              secondary={
                <Typography variant="caption" sx={{ color: MUTED }}>
                  {new Date(p.date).toLocaleDateString()} • {p.readMins} min
                </Typography>
              }
            />
          </ListItem>
        ))}
      </List>
    </Paper>
  );
}

function AboutBlogCard() {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: `1px solid ${BORDER}`,
        bgcolor: SURFACE,
        backdropFilter: "blur(6px)",
      }}
    >
      <SectionTitle
        icon={<InfoOutlinedIcon sx={{ color: ACCENT }} />}
        title="ABOUT THIS BLOG"
      />
      <Typography variant="body2" sx={{ color: MUTED }}>
        Our editorial desk blends product craftsmanship with market literacy. We
        write to shorten learning curves, demystify microstructure, and promote
        safe, confident participation in digital-asset markets.
      </Typography>
    </Paper>
  );
}

export default function BlogHome({
  brandName = "1CryptoX",
  canonical = "https://www.1cryptox.com/blog",
  lastUpdatedISO = new Date().toISOString(),
}) {
  const navigate = useNavigate();

  const lastUpdated = useMemo(() => {
    try {
      const d = new Date(lastUpdatedISO);
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "2-digit",
      });
    } catch {
      return lastUpdatedISO;
    }
  }, [lastUpdatedISO]);

  // SEO
  const title = `Blog • ${brandName}`;
  const description =
    "Insights, tutorials, product updates, and security best practices for serious traders and builders.";
  const keywords = [
    "crypto blog",
    "market insights",
    "spot trading",
    "futures",
    "ai trading",
    "security",
    "compliance",
    "api",
    brandName,
  ];

  useSEO({
    title,
    description,
    keywords,
    canonical,
    robots: "index,follow",
    og: {
      type: "website",
      title,
      description,
      url: canonical,
      image: "https://www.1cryptox.com/og/og-blog.jpg",
      siteName: brandName,
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
      image: "https://www.1cryptox.com/og/og-blog.jpg",
    },
  });

  useJsonLD("blog", {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: `${brandName} Blog`,
    url: canonical,
    description,
  });

  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [tag, setTag] = useState("");

  const featured = POSTS.filter((p) => p.featured).slice(0, 3);
  const latest = [...POSTS].sort(
    (a, b) => +new Date(b.date) - +new Date(a.date)
  );

  const filtered = latest.filter((p) => {
    const matchesCat = category === "all" ? true : p.category === category;
    const matchesTag = tag ? p.tags.includes(tag) : true;
    const matchesQuery =
      !query ||
      p.title.toLowerCase().includes(query.toLowerCase()) ||
      p.excerpt.toLowerCase().includes(query.toLowerCase());
    return matchesCat && matchesTag && matchesQuery;
  });

  // Navigation
  const handleRead = useCallback(
    (post) => {
      if (post?.id) navigate(`/blog/${post.id}`);
    },
    [navigate]
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(60% 60% at 70% 30%, rgba(240,185,11,0.10) 0%, rgba(240,185,11,0.03) 100%)",
        bgcolor: "#0B0E11",
      }}
    >
      {/* Hero */}
      <Box sx={{ borderBottom: `1px solid ${BORDER}` }}>
        <Container sx={{ py: { xs: 5, md: 8 } }}>
          <Stack spacing={2}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 0.5,
                borderRadius: 999,
                border: `1px solid rgba(240,185,11,0.35)`,
                bgcolor: "rgba(240,185,11,0.08)",
                color: ACCENT,
                fontSize: 12,
                letterSpacing: 0.6,
                fontWeight: 800,
                width: "fit-content",
              }}
            >
              INSIGHTS & UPDATES
            </Box>
            <Typography
              variant="h3"
              sx={{ color: TEXT, fontWeight: 900, letterSpacing: 0.2 }}
            >
              {brandName} Blog
            </Typography>
            <Typography variant="body1" sx={{ color: MUTED, maxWidth: 900 }}>
              Deep-dive market insights, transparent product updates, security
              first principles, and pragmatic tutorials. We write for
              practitioners — builders and traders who value clarity, pace, and
              guardrails.
            </Typography>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems="stretch"
            >
              <TextField
                fullWidth
                placeholder="Search articles, guides, and release notes…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={{
                  "& .MuiInputBase-root": {
                    bgcolor: PANEL_BG,
                    color: TEXT,
                    borderRadius: 2,
                  },
                  "& fieldset": { borderColor: BORDER },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: ACCENT }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                sx={{
                  bgcolor: ACCENT,
                  color: "#0B0E11",
                  fontWeight: 900,
                  px: 3,
                  borderRadius: 2,
                }}
              >
                Subscribe
              </Button>
            </Stack>
            <Stack spacing={1}>
              <CategoryPills current={category} onChange={setCategory} />
              <TagCloud selectedTag={tag} onSelect={setTag} />
            </Stack>
            <Typography variant="caption" sx={{ color: MUTED }}>
              Last updated: {lastUpdated}
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* Body */}
      <Container sx={{ py: { xs: 5, md: 8 } }}>
        <Grid container spacing={4}>
          {/* Main */}
          <Grid item xs={12} md={8} order={{ xs: 2, md: 1 }}>
            {/* Featured */}
            <Box sx={{ mb: 3 }}>
              <SectionTitle
                icon={<LocalFireDepartmentIcon sx={{ color: ACCENT }} />}
                title="FEATURED"
              />
              <FeaturedGrid posts={featured} onRead={handleRead} />
            </Box>

            {/* Latest / Filtered */}
            <Box sx={{ mt: 4 }}>
              <SectionTitle
                icon={<ArticleIcon sx={{ color: ACCENT }} />}
                title="LATEST POSTS"
              />
              <Stack spacing={2.5}>
                {filtered.map((p) => (
                  <PostRow key={p.id} post={p} onRead={handleRead} />
                ))}
                {filtered.length === 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      bgcolor: SURFACE,
                      border: `1px solid ${BORDER}`,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: MUTED }}>
                      No results matched your filters. Try clearing the search
                      or choosing another category/tag.
                    </Typography>
                  </Paper>
                )}
              </Stack>
            </Box>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4} order={{ xs: 1, md: 2 }}>
            <NewsletterCard />
            <Box sx={{ height: 16 }} />
            <TrendingList
              posts={[...POSTS]
                .sort((a, b) => b.trendScore - a.trendScore)
                .slice(0, 6)}
            />
            <Box sx={{ height: 16 }} />
            <AboutBlogCard />
          </Grid>
        </Grid>
      </Container>

      {/* Footer callout */}
      <Box sx={{ borderTop: `1px solid ${BORDER}`, py: 4 }}>
        <Container>
          <Typography variant="h6" sx={{ color: TEXT, fontWeight: 800, mb: 1 }}>
            Write with us
          </Typography>
          <Typography variant="body2" sx={{ color: MUTED, maxWidth: 900 }}>
            We welcome well-researched perspectives from builders and power
            users. Pitch your outline and datasets — editorial standards apply.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
